import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { AvMessagePopover, AvToggle, AvTooltip, rdpStyles, Select, useAvContext } from '@fabric-ui/components';
import { Edit, Refresh } from '@fabric-ui/icons';
import { InfoFullBlue } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, IconButton, SxProps, Typography, useTheme } from '@mui/material';
import { DayPicker, DayPickerMultipleProps } from 'react-day-picker';
import { DAY_HOURS, DEFAULT_AUTO_HOUR, DEFAULT_CRON, HALF_DAY_HOURS, HOUR_MINUTES } from './constants';
import { getTimeOptions } from './getTimeOptions';
import {
  CronEnum,
  customFrequencyOptions,
  customFrequencyOptionsLimitedLookback,
  CustomFrequencyTypes,
  CustomFrequencyTypesNames,
  defaultFrequencyTypes,
  FrequencyTypes,
  getCronFromObject,
  getCronType,
  minutesToTimeObject,
  weekOptions,
} from './utils';

const DEFAULT_MONTH = new Date(2025, 2); // March - generic 31 days month
const DatePickerTitle = () => (
  <Typography variant="caption" sx={{ color: t => t.palette.colors.neutrals[600] }} fontSize={13}>
    Repeat On
  </Typography>
);

type CronParams = { frequency?: FrequencyTypes; week: string[]; time: string[]; customTime?: any; daysInMonth: number[] };

// TODO: Move this to assets/icons
const Reset = () => <Refresh style={{ transform: 'scaleX(-1)' }} />;

const emptyCronParams: CronParams = {
  frequency: FrequencyTypes.NONE,
  week: ['0'],
  time: [`${new Date().getHours()}:0`],
  customTime: { type: CustomFrequencyTypes.HOURS },
  daysInMonth: [],
};

function getCronParams(cron: string | null, hideCustomDays?: boolean): CronParams {
  if (!cron) {
    return emptyCronParams;
  }
  const cronArray = (cron || DEFAULT_CRON).split(' ');
  const min: string = cronArray[CronEnum.MINUTE];
  const hour: string = cronArray[CronEnum.HOUR];
  const weekDays: string = cronArray[CronEnum.WEEKDAYS];
  const cronType = getCronType(cronArray);
  const daysInMonth: string = cronArray[CronEnum.DAY_OF_MONTH];

  return {
    frequency: cronType.frequency,
    week: cron && !['*', '?'].includes(weekDays) ? weekDays.split(',') : ['0'],
    time: cron && hour !== '*' && min !== '*' ? hour.split(',').map(h => `${h}:${min}`) : ['0:0'],
    customTime:
      cronType.customFrequencyTypes && cronType.value
        ? { value: cronType.value, type: cronType.customFrequencyTypes }
        : { value: 1, type: hideCustomDays ? CustomFrequencyTypes.HOURS : CustomFrequencyTypes.DAYS },
    daysInMonth: !(daysInMonth.includes('*') || daysInMonth.includes('?')) ? daysInMonth.split(',').map(d => parseInt(d)) : [],
  };
}

const getSelectedDaysInMonth = (days: number[]) =>
  days.map(d => {
    const date = new Date(DEFAULT_MONTH);
    date.setDate(Number(d));
    return date;
  }) || [];

export interface SchedulerSettingProps {
  cron: string | null;
  isAuto?: boolean;
  setIsAuto?: Dispatch<boolean>;
  setCron: Dispatch<string | null>;
  lookBackInMinutes?: number;
  setLookBackInMinutes?: Dispatch<SetStateAction<number>>;
  cronOptions?: {
    allowNone?: boolean;
    hideCustomDays?: boolean;
    shouldHideCustom?: (cronParams: CronParams) => boolean;
  };
  limitLookback?: boolean;
  isMultipleHours?: boolean;
  timesOfDayLimit?: number;
  isCustomDisabled?: boolean;
  frequencyLabel?: React.ReactNode | string;
  customTimeTypes?: CustomFrequencyTypesNames[];
  sx?: SxProps;
  disableFrequencySelector?: boolean;
}

export const SchedulerSetting: React.FC<SchedulerSettingProps> = ({
  cron,
  isAuto = false,
  setIsAuto = noop,
  setCron,
  lookBackInMinutes = 0,
  setLookBackInMinutes = noop,
  cronOptions: { allowNone, hideCustomDays, shouldHideCustom } = {},
  limitLookback = false,
  isMultipleHours = false,
  timesOfDayLimit,
  isCustomDisabled = false,
  frequencyLabel = 'Frequency',
  customTimeTypes = Object.keys(CustomFrequencyTypes),
  sx = {},
  disableFrequencySelector = false,
}) => {
  const { accountData } = useAvContext();
  const theme = useTheme();
  const autoScheduleTime = [`${accountData?.autoScheduleHour ?? DEFAULT_AUTO_HOUR}:0`];
  const cronParams: CronParams = getCronParams(allowNone && !isAuto && !cron ? null : (cron ?? DEFAULT_CRON), hideCustomDays);
  const [frequency, setFrequency] = useState<FrequencyTypes | undefined>(cronParams.frequency);
  const [week, setWeek] = useState<string[]>(cronParams.week);
  const [time, setTime] = useState<string[]>(isAuto ? autoScheduleTime : cronParams.time);
  const [customTime, setCustomTime] = useState(cronParams.customTime);
  const [daysInMonth, setDaysInMonth] = useState(cronParams.daysInMonth);

  useEffect(() => {
    setFrequency(cronParams.frequency);
    setWeek(cronParams.week);
    setTime(isAuto ? autoScheduleTime : cronParams.time);
    setCustomTime(cronParams.customTime);
    setDaysInMonth(cronParams.daysInMonth);
  }, [isAuto, cron]);

  useEffect(() => {
    if (frequency === FrequencyTypes.CUSTOM) {
      setIsAuto(false);
    }
  }, [frequency]);

  const lookbackCategoriesOptions =
    limitLookback &&
    frequency === FrequencyTypes.CUSTOM &&
    (customTime.type === CustomFrequencyTypes.HOURS || customTime.type === CustomFrequencyTypes.MINUTES)
      ? Object.values(CustomFrequencyTypes).filter(v => v !== CustomFrequencyTypes.DAYS)
      : Object.values(CustomFrequencyTypes);

  const lookBackValuesOptions =
    customTime.type === CustomFrequencyTypes.MINUTES && limitLookback
      ? customFrequencyOptionsLimitedLookback[minutesToTimeObject(lookBackInMinutes).type]
      : customFrequencyOptions[minutesToTimeObject(lookBackInMinutes).type];

  const onChangeCustomTime = field => value => setCustomTime({ ...customTime, [field]: value });

  useEffect(() => {
    setCron(getCronFromObject(cron, isAuto ? autoScheduleTime : time, frequency, week, customTime, daysInMonth.length ? daysInMonth : [1]));
  }, [isAuto, week, time, frequency, customTime, daysInMonth]);

  const handleSetCustomTimeUpdate = type => {
    setCustomTime({ type, value: customFrequencyOptions[type][0].value });
    if (limitLookback && type === CustomFrequencyTypes.HOURS && lookBackInMinutes >= DAY_HOURS * HOUR_MINUTES) {
      setLookBackInMinutes((DAY_HOURS - 1) * HOUR_MINUTES);
    } else if (limitLookback && type === CustomFrequencyTypes.MINUTES && lookBackInMinutes >= HALF_DAY_HOURS * HOUR_MINUTES) {
      setLookBackInMinutes((HALF_DAY_HOURS - 1) * HOUR_MINUTES);
    }
  };

  const getCustomTimeTypes = () =>
    Object.keys(CustomFrequencyTypes)
      .filter(key => customTimeTypes.includes(key) && (!hideCustomDays || key !== CustomFrequencyTypesNames.DAYS))
      .map(key => CustomFrequencyTypes[key]);

  const options = useMemo(
    () =>
      Object.values(FrequencyTypes).filter(
        v => ((!isAuto && allowNone) || v !== FrequencyTypes.NONE) && (!shouldHideCustom?.(cronParams) || v !== FrequencyTypes.CUSTOM)
      ),
    [allowNone, shouldHideCustom]
  );

  const handleDayInMonthSelect: DayPickerMultipleProps['onSelect'] = (days = []) => {
    if (days.length < 1) {
      return;
    }
    setDaysInMonth(days?.map(date => date.getDate()));
  };

  const showSelectTime =
    (frequency && [FrequencyTypes.WEEKLY, FrequencyTypes.DAILY, FrequencyTypes.MONTHLY].includes(frequency)) ||
    (frequency === FrequencyTypes.CUSTOM && customTime.type === CustomFrequencyTypes.DAYS);

  return (
    <Box
      sx={
        {
          ...flex.colJustifyCenter,
          gap: '10px',
          margin: 0,
          ...rdpStyles(theme, false),
          ...sx,
          display: frequency === FrequencyTypes.MONTHLY ? 'grid' : 'flex',
          gridTemplateColumns: 'auto 1fr',
          gridTemplateRows: 'auto 1fr',
        } as SxProps
      }>
      <Select
        label={frequencyLabel}
        muiProps={isMultipleHours ? undefined : { sx: { width: 150 } }}
        value={frequency}
        onChange={setFrequency}
        options={options as any[]}
        getValueFunc={(v: any) => v}
        getLabelFunc={(v: any) => v}
        isRequired
        disabled={disableFrequencySelector}
        size="small"
      />
      {frequency === FrequencyTypes.WEEKLY && (
        <AvToggle label="Repeat On" value={week} options={weekOptions} multiSelect onChange={setWeek} size="smallOutline" isRequired />
      )}
      {frequency === FrequencyTypes.MONTHLY && (
        <DayPicker
          mode="multiple"
          weekStartsOn={6}
          disableNavigation
          selected={getSelectedDaysInMonth(daysInMonth)}
          onSelect={handleDayInMonthSelect}
          hideHead
          month={DEFAULT_MONTH}
          styles={{ root: { margin: 0, marginLeft: '42px', gridRow: 'span 2', gridColumn: '2' } }}
          components={{ Caption: DatePickerTitle }}
        />
      )}
      {showSelectTime && (
        <Box
          sx={{
            width: 'calc(100% + 40px)',
            gridColumn: '1',
            ...flex.row,
            order: frequency === FrequencyTypes.CUSTOM ? 2 : undefined,
          }}>
          <Select
            readOnly={isAuto}
            label="Time (UTC)"
            muiProps={getFrequencyMuiProps(isMultipleHours, timesOfDayLimit, time) as any}
            value={isMultipleHours ? time : time[0]}
            onChange={val => setTime(isMultipleHours ? (val.length ? val : ['0:0']) : [val])}
            options={getTimeOptions(false, isMultipleHours ? 60 : undefined, isAuto ? 'Auto: ' : '') as any}
            isRequired
            isMultiple={isMultipleHours}
            size="small"
          />
          <AvTooltip title={isAuto ? 'Edit' : 'Reset to Default'}>
            <IconButton
              sx={frequency === FrequencyTypes.MONTHLY ? { bottom: '21px', left: '8px' } : { alignSelf: 'center', top: 12, left: 8 }}
              onClick={() => setIsAuto?.(!isAuto)}>
              {isAuto ? <Edit /> : <Reset />}
            </IconButton>
          </AvTooltip>
        </Box>
      )}

      {frequency === FrequencyTypes.CUSTOM && (
        <Box sx={{ ...flex.itemsEnd, gap: 2 }}>
          <Select
            label="Every"
            muiProps={{ sx: { width: 70 } }}
            value={customTime.value}
            onChange={v => onChangeCustomTime('value')(v)}
            options={customFrequencyOptions[customTime.type]}
            isRequired
            disabled={isCustomDisabled}
            size="small"
          />
          <Box sx={{ ...flex.center, gap: 1 }}>
            <Select
              muiProps={{ sx: { width: 110 } }}
              value={customTime.type}
              onChange={type => handleSetCustomTimeUpdate(type)}
              options={getCustomTimeTypes()}
              getValueFunc={(v: any) => v}
              getLabelFunc={(v: any) => v}
              isRequired
              disabled={isCustomDisabled}
              size="small"
            />
            <AvMessagePopover
              message={isCustomDisabled ? 'Contact support for custom scheduling' : 'Starting automatically within the next 15 min'}
              icon={<InfoFullBlue />}
              sx={{ height: 40, borderRadius: '8px', ...flex.center }}
              asTooltip
            />
          </Box>
        </Box>
      )}
      {lookBackInMinutes && setLookBackInMinutes ? (
        <Box sx={{ ...flex.itemsEnd, gap: 2 }}>
          <Select
            label="Data from the Last"
            muiProps={{ sx: { width: 150 } }}
            value={lookBackInMinutes / defaultFrequencyTypes[minutesToTimeObject(lookBackInMinutes).type]}
            onChange={v => setLookBackInMinutes(v * defaultFrequencyTypes[minutesToTimeObject(lookBackInMinutes).type])}
            options={lookBackValuesOptions}
            isRequired
            size="small"
          />
          <Select
            style={{ paddingTop: 3 }}
            muiProps={{ sx: { width: 110 } }}
            value={minutesToTimeObject(lookBackInMinutes).type}
            onChange={type => setLookBackInMinutes(customFrequencyOptions[type][0].value * defaultFrequencyTypes[type])}
            options={lookbackCategoriesOptions as any}
            getValueFunc={(v: any) => v}
            getLabelFunc={(v: any) => v}
            isRequired
            size="small"
          />
        </Box>
      ) : null}
    </Box>
  );
};

const getFrequencyMuiProps = (isMultipleHours: boolean, timesOfDayLimit: number | undefined, time: string[]) =>
  isMultipleHours
    ? { getOptionDisabled: timesOfDayLimit ? ({ value }) => time.length >= timesOfDayLimit && !time.includes(value) : undefined }
    : { sx: { width: 150 } };
